import { Share, Tune } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import type React from "react";
import { useState } from "react";
import { tokens } from "../../../../../../../../theme";
import TawkChat from "../../../../../../../landing/components/TawkChat";
import ConferenceSidePanel from "../../../shared-ui/ConferenceSidePanel";
import EventHeader from "../../../shared-ui/EventHeader";
import MemorialFooter from "../../../shared-ui/MemorialFooter";
import ServiceDetails from "../../../shared-ui/ServiceDetails";
import ShareControls from "../../../shared-ui/ShareControls";
import ShareDrawer from "../../../shared-ui/ShareDrawer";
import StreamViewer from "../../../shared-ui/StreamViewer";
import TemplateSelectorDialog from "../../../shared-ui/TemplateSelectorDialog";
import type { CalendarEvent } from "../../../types/AddtoCalendarutils";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import type { IStreamDetails, IThemeConfig } from "../../../types/memorial-types";

export interface ConferenceTemplate1Props {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedName?: string;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  streamProgram: string | null;
  downloadingProgram: boolean;
  handleDownloadProgram: () => void;

  onDonationSuccess?: (donationId?: number) => void;

  conferenceCalendarEvent: CalendarEvent | null;

  fetchStreamDetails: () => Promise<void>;

  contactPhone?: string;
  contactEmail?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  whatsappUrl?: string;
}

export default function ConferenceTemplate1({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedName,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  streamProgram,
  downloadingProgram,
  handleDownloadProgram,
  onDonationSuccess,
  conferenceCalendarEvent,
  fetchStreamDetails,
  contactPhone,
  contactEmail,
  facebookUrl,
  linkedinUrl,
  youtubeUrl,
  whatsappUrl,
}: ConferenceTemplate1Props) {
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: memorialTheme.surface }}>
      <TawkChat />
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}>
          <Tooltip title="Change page template">
            <Button
              size="small"
              startIcon={<Tune fontSize="small" />}
              onClick={() => setTemplateDialogOpen(true)}
              sx={{ textTransform: "none", borderRadius: "50px" }}
            >
              Change Template
            </Button>
          </Tooltip>
        </Box>
      )}
      <EventHeader stream={currentStream} theme={memorialTheme} />

      <TemplateSelectorDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        serviceType={currentStream.booking.service}
        streamId={currentStream.id}
        currentTemplateId={currentStream.template_id}
        onConfirm={() => {
          setTemplateDialogOpen(false);
          fetchStreamDetails();
        }}
      />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ py: { xs: 2, md: 4 } }}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          {isMobile ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <StreamViewer
                  streams={streams}
                  activeStream={activeStreamIndex}
                  onStreamChange={onStreamChange}
                  memorialTheme={memorialTheme}
                  currentStream={currentStream}
                  deceasedName={deceasedName}
                  hideTabs
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Button
                    variant="contained"
                    sx={{ borderRadius: "50px", textTransform: "none" }}
                    onClick={handleDownloadProgram}
                    disabled={!streamProgram}
                  >
                    {downloadingProgram ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                        Download Program
                      </Typography>
                    )}
                  </Button>

                  <IconButton
                    onClick={() => setShareDrawerOpen(true)}
                    sx={{
                      bgcolor: "info.main",
                      color: "#fff",
                      width: 40,
                      height: 40,
                      "&:hover": { bgcolor: "info.light" },
                    }}
                  >
                    <Share />
                  </IconButton>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <ServiceDetails stream={currentStream} theme={memorialTheme} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <ConferenceSidePanel
                  streams={streams}
                  activeStream={activeStreamIndex}
                  onStreamChange={onStreamChange}
                  memorialTheme={memorialTheme}
                  messages={messages}
                  isAdmin={isAdmin}
                  socketRef={socketRef}
                  isMobile={isMobile}
                  handleApprove={handleApprove}
                  handleDelete={handleDelete}
                  colors={colors}
                  onMessageSent={onMessageSent}
                />
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <StreamViewer
                    streams={streams}
                    activeStream={activeStreamIndex}
                    onStreamChange={onStreamChange}
                    memorialTheme={memorialTheme}
                    currentStream={currentStream}
                    deceasedName={deceasedName}
                    hideTabs
                  />

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1 }}
                    flexWrap="wrap"
                  >
                    <ShareControls
                      streamData={currentStream}
                      theme={memorialTheme}
                      donationsEnabled={currentStream?.go_fund_me_enabled || false}
                      streamProgram={streamProgram}
                      handleDownloadProgram={handleDownloadProgram}
                      downloadingProgram={downloadingProgram}
                      onDonationSuccess={onDonationSuccess}
                      calendarEvent={conferenceCalendarEvent}
                    />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ConferenceSidePanel
                    streams={streams}
                    activeStream={activeStreamIndex}
                    onStreamChange={onStreamChange}
                    memorialTheme={memorialTheme}
                    messages={messages}
                    isAdmin={isAdmin}
                    socketRef={socketRef}
                    isMobile={isMobile}
                    handleApprove={handleApprove}
                    handleDelete={handleDelete}
                    colors={colors}
                    onMessageSent={onMessageSent}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <ServiceDetails stream={currentStream} theme={memorialTheme} />
                </Grid>
              </Grid>
            </>
          )}

          <ShareDrawer
            open={shareDrawerOpen}
            onClose={() => setShareDrawerOpen(false)}
            streamData={currentStream}
          />
        </motion.div>
      </Container>
      <MemorialFooter
        theme={memorialTheme}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        facebookUrl={facebookUrl}
        linkedinUrl={linkedinUrl}
        youtubeUrl={youtubeUrl}
        whatsappUrl={whatsappUrl}
      />
    </Box>
  );
}
